import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {AntibodyChain, AntibodyFormat, AntibodyVersion} from 'antibodies/AntibodyTypedefs';
import {chainColor, getChainLabel} from 'antibodies/AntibodyUtils';
import AntibodyComponentPopover from 'antibodies/visualization/AntibodyComponentPopover';
import InteractiveComponentWrapper, {
  AntibodyInteractivityProps,
} from 'antibodies/visualization/InteractiveComponentWrapper';
import {assert} from 'Asserts';
import {AntibodyChainType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';

interface ChainLegendItemProps {
  chainRef: number;
  chainIndex: number;
  chainType: AntibodyChainType;
  label: string;
  entity?: AntibodyChain['entity'];
  interactivity?: AntibodyInteractivityProps;
  antibodyId?: string;
}

function ChainLegendItem({
  chainRef,
  chainIndex,
  chainType,
  label,
  entity,
  interactivity,
  antibodyId,
}: ChainLegendItemProps): JSX.Element {
  const inaccessible = entity != null && isInaccessible(entity);
  const proteinId = entity != null && !inaccessible ? entity.id : null;

  const legendItem = (
    <div className={css(styles.legendItem)}>
      <svg width={16} height={16}>
        <circle
          cx={8}
          cy={8}
          fill={chainColor(chainIndex, chainType)}
          r={7}
          {...getTestProps({component: 'AntibodyChainLegend', element: 'circle', key: chainRef.toString()})}
        />
      </svg>
      <span className={css(styles.legendItemText)}>{label}</span>
    </div>
  );
  return entity != null ? (
    <AntibodyComponentPopover
      proteinId={proteinId}
      inaccessible={inaccessible}
      componentLabel={label}
      showPopovers={proteinId != null}
      antibodyId={antibodyId}
    >
      <InteractiveComponentWrapper
        antibodyId={antibodyId}
        componentRef={chainRef}
        interactivity={interactivity}
        interactiveElement="div"
        componentLabel={label}
      >
        {legendItem}
      </InteractiveComponentWrapper>
    </AntibodyComponentPopover>
  ) : (
    legendItem
  );
}

export interface AntibodyChainLegendProps {
  antibodyFormat: AntibodyFormat;
  antibodyVersion?: AntibodyVersion;
  interactivity?: AntibodyInteractivityProps;
  antibodyId?: string;
}

export default function AntibodyChainLegend({
  antibodyFormat,
  antibodyVersion,
  interactivity,
  antibodyId,
}: AntibodyChainLegendProps): JSX.Element {
  return (
    <div className={css(styles.legend)}>
      {antibodyFormat.distinctChains.map((chain, chainIndex) => {
        let matchingChains: Array<AntibodyChain> = [];
        if (antibodyVersion != null) {
          matchingChains = antibodyVersion.chains.filter((c) => c.label === chain.label);
          assert(matchingChains.length >= 1, 'Expected at least one format chain for a distinct chain');
        }
        const matchingEntities = new Set(matchingChains.map((c) => c.entity));
        if (antibodyVersion != null) {
          assert(matchingEntities.size === 1, 'Expected exactly one entity for a distinct chain');
        }
        return (
          <ChainLegendItem
            key={chainIndex}
            // We select the first ref here, since antibody map interactivity currently tracks a single ref
            // (ref ordering is deterministic).
            // Ideally, we would track interactivity at the distinct component level (or track multiple refs,
            // for each duplicate component).
            chainRef={chain.refs[0]}
            chainIndex={chainIndex}
            chainType={chain.type}
            label={getChainLabel(chain.label)}
            entity={Array.from(matchingEntities)[0]}
            interactivity={interactivity}
            antibodyId={antibodyId}
          />
        );
      })}
    </div>
  );
}

const styles = StyleSheet.create({
  legend: {
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
    flexWrap: 'wrap',
    border: `1px solid ${Color.GRAY_200}`,
    borderRadius: '4px',
    padding: '8px',
    justifyContent: 'space-evenly',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  legendItemText: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    letterSpacing: '0',
  },
});
